import { router } from "@inertiajs/react"
import { Plus, X } from "lucide-react"
import { useState } from "react"

import { formatPhone } from "@/components/agents/setup/receptionist/phone-utils"
import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import type { SetupStepProps } from "@/components/agents/setup/types"
import { YesNoCards } from "@/components/agents/setup/yes-no-cards"
import { WeeklyHoursGrid } from "@/components/business-profile/hours-editor"
import type { DayHours } from "@/components/business-profile/hours-editor"
import { FormField } from "@/components/form-field"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { setupAgentPath } from "@/routes"

// Keys must match Agents::Receptionist::TransferRules on the server.
const K_ENABLED = "transfers_enabled"
const K_PRESETS = "handoff_presets"
const K_CUSTOM = "handoff_custom_reasons"
const K_PHONE = "handoff_phone"
// IMP-491: the weekly window transfers are allowed in.
const K_HOURS = "handoff_hours"

function readBool(data: Record<string, unknown>, key: string): boolean {
  return data[key] === true
}

function readStringArray(data: Record<string, unknown>, key: string): string[] {
  const value = data[key]
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : []
}

function readString(data: Record<string, unknown>, key: string): string {
  const value = data[key]
  return typeof value === "string" ? value : ""
}

export function TransfersStep({
  agent,
  stepNumber,
  totalSteps,
  transferPresets,
  transferHoursPrefill,
}: SetupStepProps) {
  const [enabled, setEnabled] = useState(() =>
    readBool(agent.setup_data, K_ENABLED),
  )
  const [selectedPresets, setSelectedPresets] = useState<string[]>(() =>
    readStringArray(agent.setup_data, K_PRESETS),
  )
  const [customReasons, setCustomReasons] = useState<string[]>(() =>
    readStringArray(agent.setup_data, K_CUSTOM),
  )
  const [phone, setPhone] = useState<string>(() =>
    readString(agent.setup_data, K_PHONE),
  )
  // IMP-491: a saved schedule always wins over the prefill, which is only the
  // starting point the server mirrors from the business hours.
  const [hours, setHours] = useState<DayHours[]>(() => {
    const saved = agent.setup_data[K_HOURS]
    return Array.isArray(saved) && saved.length > 0
      ? (saved as DayHours[])
      : transferHoursPrefill
  })
  const [processing, setProcessing] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  function togglePreset(id: string) {
    setSelectedPresets((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    )
  }

  function updateCustomReason(index: number, value: string) {
    setCustomReasons((prev) => prev.map((r, i) => (i === index ? value : r)))
  }

  function addCustomReason() {
    setCustomReasons((prev) => [...prev, ""])
  }

  function removeCustomReason(index: number) {
    setCustomReasons((prev) => prev.filter((_, i) => i !== index))
  }

  function submit(nextStep: "step-appointments" | "step-connect") {
    setProcessing(true)
    router.patch(
      setupAgentPath({ id: agent.uid }),
      {
        setup_step: nextStep,
        setup_data: {
          [K_ENABLED]: enabled,
          [K_PRESETS]: selectedPresets,
          [K_CUSTOM]: customReasons
            .map((r) => r.trim())
            .filter((r) => r.length > 0),
          [K_PHONE]: phone,
          [K_HOURS]: hours,
        },
      },
      {
        preserveScroll: true,
        onError: (errors) => setFieldErrors(errors as Record<string, string>),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const hasRule =
    selectedPresets.length > 0 || customReasons.some((r) => r.trim().length > 0)
  const canContinue = !enabled || (hasRule && phone.trim().length > 0)

  return (
    <WizardLayout
      footer={
        <>
          <Button variant="outline" onClick={() => submit("step-appointments")}>
            Back
          </Button>
          <Button
            onClick={() => submit("step-connect")}
            disabled={processing || !canContinue}
          >
            Continue
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Should I transfer calls?"
        description="When the situation calls for it, I can put a caller through to a person."
      />
      <YesNoCards
        yesLabel="Yes, transfer calls"
        yesSub="I'll put callers through to a person while they're still on the line."
        noLabel="No, don't transfer calls"
        noSub="I'll answer questions, but won't put anyone through."
        value={enabled}
        onChange={setEnabled}
      />

      {enabled && (
        <>
          <FormField label="Transfer when…" error={fieldErrors[K_PRESETS]}>
            <div className="space-y-4">
              {transferPresets.map(({ id, label }) => (
                <div key={id} className="flex items-center gap-3">
                  <Checkbox
                    id={`transfer-${id}`}
                    checked={selectedPresets.includes(id)}
                    onCheckedChange={() => togglePreset(id)}
                  />
                  <Label
                    htmlFor={`transfer-${id}`}
                    className="cursor-pointer font-normal"
                  >
                    {label}
                  </Label>
                </div>
              ))}
              {customReasons.map((reason, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Checkbox checked />
                  <input
                    className="h-4 flex-1 cursor-text border-none bg-transparent p-0 text-sm leading-none ring-0 outline-none"
                    value={reason}
                    onChange={(e) => updateCustomReason(i, e.target.value)}
                    placeholder="Describe the reason…"
                  />
                  <button
                    type="button"
                    onClick={() => removeCustomReason(i)}
                    className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                    aria-label="Remove"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addCustomReason}
                className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm transition-colors"
              >
                <Plus className="size-4" /> Add custom reason
              </button>
            </div>
          </FormField>

          <FormField
            label="Transfer calls to"
            htmlFor="handoff_phone"
            error={fieldErrors[K_PHONE]}
          >
            <Input
              id="handoff_phone"
              type="tel"
              placeholder="(555) 000-0000"
              className="max-w-xs"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
            />
          </FormField>

          <FormField label="Available hours" error={fieldErrors[K_HOURS]}>
            {/* IMP-491: the shared weekly grid, with no 24h option — the
                transfer window has no all-day concept, so a day is either
                unavailable or has a start and an end. */}
            <WeeklyHoursGrid
              hours={hours}
              allowAllDay={false}
              closedLabel="Unavailable"
              onHourChange={(idx, patch) =>
                setHours((prev) =>
                  prev.map((row, i) =>
                    i === idx ? { ...row, ...patch } : row,
                  ),
                )
              }
            />
          </FormField>
        </>
      )}
    </WizardLayout>
  )
}
